import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AchievementDefinition } from '@/redux/achievement/achievement.types';
import { AchievementCategory, groupByCategory } from './achievementCategories';
import { CoinMark, Stamp } from './AchievementParts';
import {
  BadgeState,
  stateOf,
  useProfileAchievements,
} from './useProfileAchievements';

interface Row {
  entry: AchievementDefinition;
  state: BadgeState;
}

const TILE_TONE: Record<
  BadgeState,
  { tile: string; name: string; xp: string }
> = {
  earned: {
    tile: 'border-green-100 bg-card hover:border-green-200',
    name: '',
    xp: 'text-green-800',
  },
  locked: {
    tile: 'border-transparent bg-neutral-50 hover:bg-neutral-100',
    name: 'text-muted-foreground',
    xp: 'text-muted-foreground',
  },
  soon: {
    tile: 'border-transparent bg-neutral-50 hover:bg-neutral-100',
    name: 'text-muted-foreground',
    xp: 'text-muted-foreground',
  },
};

const badgeLabel = (entry: AchievementDefinition) =>
  entry.coins
    ? `${entry.name} achievement, pays ${entry.coins} reward points`
    : `${entry.name} achievement`;

const badgeTip = (row: Row) =>
  row.state === 'soon' ? 'Coming soon' : row.entry.description;

function BadgeTile({ entry, state }: Row) {
  const tone = TILE_TONE[state];
  return (
    <li data-testid={`achievement-${entry.id}`} data-state={state}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={badgeLabel(entry)}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition-colors focus-visible:outline-none focus-visible:shadow-focus',
              tone.tile
            )}
          >
            <Stamp entry={entry} state={state} />
            <span className="min-w-0 flex-1">
              <span
                className={cn('block truncate text-sm font-medium', tone.name)}
              >
                {entry.name}
              </span>
              <span className="mt-0.5 flex items-center gap-2 text-xs tabular-nums">
                <span className={tone.xp}>+{entry.points} XP</span>
                <CoinMark entry={entry} />
              </span>
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-64 text-xs">
          {badgeTip({ entry, state })}
        </TooltipContent>
      </Tooltip>
    </li>
  );
}

function CategorySection({
  category,
  rows,
  total,
}: {
  category: AchievementCategory;
  rows: Row[];
  total: number;
}) {
  const earned = rows.filter((row) => row.state === 'earned').length;
  const Icon = category.icon;
  return (
    <section data-testid={`achievement-group-${category.key}`}>
      <div className="mb-3 flex items-center gap-2.5">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-green-50 text-green-700">
          <Icon className="h-4 w-4" />
        </span>
        <h3 className="font-display text-base font-semibold">
          {category.label}
        </h3>
        <span className="text-xs tabular-nums text-muted-foreground">
          {earned} / {total}
        </span>
        <span className="ml-auto h-1 w-20 overflow-hidden rounded-full bg-neutral-100">
          <span
            className="block h-full rounded-full bg-green-600"
            style={{ width: `${(earned / total) * 100}%` }}
          />
        </span>
      </div>
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
        {rows.map((row) => (
          <BadgeTile key={row.entry.id} {...row} />
        ))}
      </ul>
    </section>
  );
}

const totalsByCategory = (catalog: AchievementDefinition[]) => {
  const totals = new Map<string, number>();
  groupByCategory(catalog.map((entry) => ({ entry }))).forEach((group) =>
    totals.set(group.category.key, group.rows.length)
  );
  return totals;
};

function ViewToggle({
  showAll,
  onChange,
  total,
}: {
  showAll: boolean;
  onChange: (showAll: boolean) => void;
  total: number;
}) {
  const options = [
    { value: false, label: 'Earned' },
    { value: true, label: `All ${total}` },
  ];
  return (
    <div className="inline-flex rounded-lg bg-neutral-100 p-1">
      {options.map((option) => (
        <button
          key={option.label}
          type="button"
          aria-pressed={showAll === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            'rounded-md px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:shadow-focus',
            showAll === option.value
              ? 'bg-card text-foreground shadow-xs'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function Summary({
  earned,
  total,
  xp,
}: {
  earned: number;
  total: number;
  xp: number;
}) {
  return (
    <div className="min-w-0 flex-1">
      <p className="text-sm tabular-nums text-muted-foreground">
        <span className="font-semibold text-foreground">
          {earned} of {total}
        </span>{' '}
        earned · {xp.toLocaleString()} XP
      </p>
      <span className="mt-2 block h-1.5 max-w-xs overflow-hidden rounded-full bg-neutral-100">
        <span
          className="block h-full rounded-full bg-green-600"
          style={{ width: `${(earned / total) * 100}%` }}
        />
      </span>
    </div>
  );
}

/** Visitors see what was earned; the owner can also browse what's left. */
export default function Achievements({
  username,
  isOwnProfile,
  initialShowAll = false,
}: {
  username: string;
  isOwnProfile: boolean;
  initialShowAll?: boolean;
}) {
  const [showAll, setShowAll] = useState(initialShowAll);
  const data = useProfileAchievements(username);
  if (!data) return null;

  const rows = data.catalog
    .map((entry) => ({ entry, state: stateOf(entry, data.earned) }))
    .filter((row) => showAll || row.state === 'earned');
  const totals = totalsByCategory(data.catalog);

  return (
    <div className="flex flex-col gap-6" data-testid="achievements">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Summary
          earned={data.earned.size}
          total={data.catalog.length}
          xp={data.totalPoints}
        />
        {isOwnProfile && (
          <ViewToggle
            showAll={showAll}
            onChange={setShowAll}
            total={data.catalog.length}
          />
        )}
      </div>
      {rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No achievements earned yet.
        </p>
      ) : (
        <TooltipProvider delayDuration={200}>
          {groupByCategory(rows).map(({ category, rows: groupRows }) => (
            <CategorySection
              key={category.key}
              category={category}
              rows={groupRows}
              total={totals.get(category.key) ?? groupRows.length}
            />
          ))}
        </TooltipProvider>
      )}
    </div>
  );
}
